// @ts-check

import {useRef, useState, useTransition, useEffect} from 'react';
import '../jsdoc-types';
import {Button, RadioGroup, TextField, Alert} from '@bonprix/design-system';
import Url from '@core/scripts/helper/url';
import fetchWithCsrf from '../../../../../../../core/src/main/resources/scripts/helper/fetch-with-csrf.js';
import { WidgetInstance } from "friendly-challenge";
import ModelContainer from "@core/scripts/components/model-container";

const model = ModelContainer.get("servicePageModel");
const postValues = ModelContainer.get("servicePagePostValues");

/**
 * @param {Object} props
 * @param {NewsletterSubscriptionForm} props.cdaEntry
 */
export function NewsletterSubscriptionForm({cdaEntry}) {
    const [, startTransition] = useTransition();
    const [formValues, setFormValues] = useState({
        email: postValues?.["formhandler[email]"] ?? '',
        salutation: '',
        firstName: '',
        lastName: '',
        "frc-captcha-solution": '',
        origin: cdaEntry.fields.apiOrigin
    });
    const [submitButtonIsDisabled, setSubmitButtonIsDisabled] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [formErrors, setFormErrors] = useState({});

    const fcContainer = useRef();
    const fcWidget = useRef();
    const friendlyCaptchaSitekey = model.tenantCountry === 'at' ? model.friendlyCaptchaSitekeyNewsletterAt : model.friendlyCaptchaSitekeyNewsletter;

    useEffect(() => {
        if(!model.friendlyCaptchaEnabled) {
            setSubmitButtonIsDisabled(false);
            return;
        }

        if (!fcWidget.current && fcContainer.current) {
            fcWidget.current = new WidgetInstance(fcContainer.current, {
                doneCallback: (solution) => {
                    setFormValues((prev) => ({...prev, "frc-captcha-solution": solution}));
                    setSubmitButtonIsDisabled(false);
                }
            });
        }

        return () => {
            if (fcWidget.current != undefined) fcWidget.current.reset();
        };
    }, [fcContainer]);

    const handleChange = (name) => (value) => {
        startTransition(() => setFormValues((prev) => ({...prev, [name]: value})));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setFormErrors({});
        setErrorMsg("");
        setIsSubmitting(true);

        fetchWithCsrf(Url.toAbsolute(cdaEntry.fields.subscribeApiUrl), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formValues),
        })
        .then((response) => response.json())
        .then((data) => {
            if(data.errors){
                setFormErrors(data.errors);
            }
            if(data.errorMsg){
                setErrorMsg(cdaEntry.fields[data.errorMsg]);
            }

            if(data.errors || data.errorMsg){
                fcWidget.current.reset();
            }

            //redirect to success page
            if(data.success){
                window.location= Url.toAbsolute(cdaEntry.fields.successPageUrl);
            }
        }).catch((error) => {
            console.error(error);
            setErrorMsg(cdaEntry.fields.genericErrorMessage);
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    return (
        <div className="service-page-newsletter-subscription-form">
            <form onSubmit={handleSubmit}>
                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelEmail}
                        inputMode="email"
                        type="email"
                        value={formValues.email}
                        name="email"
                        onChange={handleChange('email')}
                        error={formErrors && !!formErrors.email}
                        errorText={formErrors && formErrors.email && cdaEntry.fields[formErrors.email]}
                    />
                </div>

                <div className="form-row form-salutation-group">
                    <RadioGroup
                        label={cdaEntry.fields.labelSalutation}
                        required
                        value={formValues.salutation}
                        name="salutation"
                        onChange={handleChange('salutation')}
                        error={formErrors && !!formErrors.salutation}
                        errorText={formErrors && formErrors.salutation && cdaEntry.fields[formErrors.salutation]}
                    >
                        <RadioGroup.Item value="0" label={cdaEntry.fields.labelSalutation0} />
                        <RadioGroup.Item value="1" label={cdaEntry.fields.labelSalutation1} />
                        <RadioGroup.Item value="2" label={cdaEntry.fields.labelSalutation2} />
                    </RadioGroup>
                </div>

                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelFirstName}
                        required
                        name="firstName"
                        value={formValues.firstName}
                        onChange={handleChange('firstName')}
                        error={formErrors && !!formErrors.firstName}
                        errorText={formErrors && formErrors.firstName && cdaEntry.fields[formErrors.firstName]}
                    />
                </div>

                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelLastName}
                        required
                        name="lastName"
                        value={formValues.lastName}
                        onChange={handleChange('lastName')}
                        error={formErrors && !!formErrors.lastName}
                        errorText={formErrors && formErrors.lastName && cdaEntry.fields[formErrors.lastName]}
                    />
                </div>

                {model.friendlyCaptchaEnabled && (
                    <div className="form-row">
                        <div ref={fcContainer} className="frc-captcha" data-sitekey={friendlyCaptchaSitekey} data-lang="de"></div>
                        {formErrors && formErrors["frc-captcha-solution"] && (
                            <Alert isOpen severity="error" showCloseIcon={false}>{cdaEntry.fields[formErrors["frc-captcha-solution"]]}</Alert>
                        )}
                    </div>
                )}

                {errorMsg && (
                    <div className="form-row">
                         <Alert isOpen severity="error" showCloseIcon={false}>{errorMsg}</Alert>
                    </div>
                )}

                <div className="form-row">
                    <Button type="submit" disabled={submitButtonIsDisabled} loading={isSubmitting}>
                        {cdaEntry.fields.labelButtonSubmit}
                    </Button>
                </div>
            </form>
        </div>
    );
}
